import { useState, useEffect } from 'react';
import { API, formatWeight, formatDateTime } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DecimalInput } from '@/components/ui/decimal-input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Check, X, Printer, ShoppingBag, AlertTriangle, FileText, Truck } from 'lucide-react';

export default function PurchaseOrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [createForm, setCreateForm] = useState({ supplier_name: '', items: [], notes: '' });
  const [newItem, setNewItem] = useState({ item_name: '', quantity_g: '', gold_purity: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const canApprove = ['admin', 'store_manager', 'analyst'].includes(user?.role);

  const fetchOrders = async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await API.get('/purchase-orders', { params });
      setOrders(res.data);
    } catch(e) { console.error(e); }
  };

  const fetchSuggestions = async () => {
    try {
      const res = await API.get('/purchase-orders/suggestions');
      setSuggestions(res.data);
    } catch(e) { console.error(e); }
  };

  useEffect(() => { fetchOrders(); fetchSuggestions(); }, [statusFilter]);

  const addItemToForm = () => {
    if (!newItem.item_name || !newItem.quantity_g) { toast.error('Fill item name and quantity'); return; }
    setCreateForm(prev => ({
      ...prev,
      items: [...prev.items, { ...newItem, quantity_g: parseFloat(newItem.quantity_g) }],
    }));
    setNewItem({ item_name: '', quantity_g: '', gold_purity: '' });
  };

  const removeItemFromForm = (idx) => {
    setCreateForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const handleCreatePO = async () => {
    if (!createForm.supplier_name) { toast.error('Enter karigar name'); return; }
    if (createForm.items.length === 0) { toast.error('Add at least one item'); return; }
    setLoading(true);
    try {
      await API.post('/purchase-orders', {
        ...createForm,
        submitted_by: user?.role || 'store_manager',
      });
      toast.success('Purchase order created!');
      setShowCreate(false);
      setCreateForm({ supplier_name: '', items: [], notes: '' });
      fetchOrders();
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed to create PO'); }
    finally { setLoading(false); }
  };

  const createFromSuggestion = (supplier, items) => {
    setCreateForm({
      supplier_name: supplier,
      items: items.map(i => ({ item_name: i.item_name, quantity_g: i.suggested_order_g, gold_purity: i.gold_purity })),
      notes: 'Auto-generated from low stock suggestions',
    });
    setShowCreate(true);
  };

  const handleApproval = async (poId, action) => {
    try {
      await API.post(`/purchase-orders/${poId}/action`, {
        action, approved_by: user?.name || user?.role,
        comments: '',
      });
      toast.success(`PO ${action}`);
      fetchOrders();
      setSelectedPO(null);
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handlePrint = (po) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html><head><title>PO ${po.po_number}</title>
      <style>body{font-family:Arial,sans-serif;padding:40px;color:#1a1a1a}
      h1{font-size:24px;margin-bottom:4px} .meta{color:#666;font-size:14px;margin-bottom:24px}
      table{width:100%;border-collapse:collapse;margin-top:16px}
      th,td{border:1px solid #ddd;padding:8px 12px;text-align:left;font-size:14px}
      th{background:#f5f5f5;font-weight:600} .footer{margin-top:40px;font-size:12px;color:#999}
      .badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:12px;font-weight:600}
      .badge-pending{background:#fef3c7;color:#92400e}
      .badge-approved{background:#d1fae5;color:#065f46}
      .badge-rejected{background:#fee2e2;color:#991b1b}
      </style></head><body>
      <h1>Yash Gold Wholesale</h1>
      <p class="meta">Purchase Order: <strong>${po.po_number}</strong></p>
      <p><strong>Karigar:</strong> ${po.supplier_name}</p>
      <p><strong>Status:</strong> <span class="badge badge-${po.status}">${po.status.toUpperCase()}</span></p>
      <p><strong>Date:</strong> ${new Date(po.created_at).toLocaleDateString('en-IN')}</p>
      ${po.notes ? `<p><strong>Notes:</strong> ${po.notes}</p>` : ''}
      <table><tr><th>#</th><th>Item</th><th>Purity</th><th>Quantity (g)</th></tr>
      ${(po.items || []).map((item, i) => `<tr><td>${i+1}</td><td>${item.item_name}</td><td>${item.gold_purity || '—'}</td><td>${item.quantity_g}g</td></tr>`).join('')}
      </table>
      <div class="footer"><p>Generated on ${new Date().toLocaleString('en-IN', {timeZone:'Asia/Kolkata'})}</p></div>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const statusBadge = (status) => {
    const colors = { pending: 'bg-amber-50 text-amber-800 border-amber-200', approved: 'bg-emerald-50 text-emerald-800 border-emerald-200', rejected: 'bg-rose-50 text-rose-800 border-rose-200' };
    return <span className={`status-badge ${colors[status] || ''}`}>{status}</span>;
  };

  const hasSuggestions = Object.keys(suggestions).length > 0;

  return (
    <div data-testid="purchase-orders-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1>Purchase Orders</h1>
          <p className="hidden sm:block">Manage and track purchase orders</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="w-full sm:w-auto" data-testid="po-create-button">
          <Plus size={16} className="mr-2" />New PO
        </Button>
      </div>

      {/* Low Stock Alerts */}
      {hasSuggestions && (
        <Card className="mb-4" style={{ background: 'hsl(43, 50%, 14%)', borderColor: 'hsl(43, 50%, 25%)' }} data-testid="po-suggestions-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2" style={{ color: 'hsl(43, 80%, 65%)' }}>
              <AlertTriangle size={16} /> Low Stock Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(suggestions).map(([supplier, items]) => (
                <div key={supplier} className="p-3 rounded-lg border" style={{ background: 'hsl(30, 8%, 11%)', borderColor: 'hsl(43, 50%, 25%)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm flex items-center gap-1.5"><Truck size={14} />{supplier}</span>
                    <Button size="sm" variant="outline" onClick={() => createFromSuggestion(supplier, items)} className="text-xs h-7" data-testid={`po-create-from-suggestion-${supplier}`}>
                      <Plus size={12} className="mr-1" />Create PO
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {items.map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span>{item.item_name} {item.gold_purity && <Badge variant="outline" className="text-[10px] ml-1">{item.gold_purity}</Badge>}</span>
                        <span className="font-mono">Current: {item.current_quantity_g}g / Min: {item.min_quantity}g <span className="text-rose-600 font-semibold">Need: {item.suggested_order_g}g</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 flex-wrap">
        {['all', 'pending', 'approved', 'rejected'].map(s => (
          <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm" onClick={() => setStatusFilter(s)} className="text-xs capitalize">
            {s}
          </Button>
        ))}
      </div>

      {/* Orders List - Mobile */}
      <div className="mobile-card-list">
        {orders.map(po => (
          <Card key={po.id} className="cursor-pointer" onClick={() => setSelectedPO(po)}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono font-semibold text-sm">{po.po_number}</span>
                {statusBadge(po.status)}
              </div>
              <p className="text-xs text-muted-foreground">{po.supplier_name}</p>
              <p className="text-xs text-muted-foreground">{(po.items || []).length} items | {formatDateTime(po.created_at)}</p>
            </CardContent>
          </Card>
        ))}
        {orders.length === 0 && <p className="text-center py-8 text-sm text-muted-foreground">No purchase orders</p>}
      </div>

      {/* Orders List - Desktop */}
      <div className="desktop-table">
        <Card>
          <CardContent className="p-0">
            <div className="table-scroll-wrapper">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>PO #</TableHead>
                    <TableHead>Karigar</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map(po => (
                    <TableRow key={po.id}>
                      <TableCell className="font-mono font-semibold">{po.po_number}</TableCell>
                      <TableCell>{po.supplier_name}</TableCell>
                      <TableCell>{(po.items || []).length} items</TableCell>
                      <TableCell>{statusBadge(po.status)}</TableCell>
                      <TableCell className="text-sm">{formatDateTime(po.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => setSelectedPO(po)} data-testid={`po-view-${po.po_number}`}>
                            <FileText size={14} />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handlePrint(po)} data-testid={`po-print-${po.po_number}`}>
                            <Printer size={14} />
                          </Button>
                          {canApprove && po.status === 'pending' && (
                            <>
                              <Button variant="ghost" size="icon" className="text-emerald-600" onClick={() => handleApproval(po.id, 'approved')} data-testid={`po-approve-${po.po_number}`}>
                                <Check size={14} />
                              </Button>
                              <Button variant="ghost" size="icon" className="text-rose-600" onClick={() => handleApproval(po.id, 'rejected')} data-testid={`po-reject-${po.po_number}`}>
                                <X size={14} />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {orders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No purchase orders</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create PO Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Purchase Order</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Karigar Name *</label>
              <Input value={createForm.supplier_name} onChange={e => setCreateForm({...createForm, supplier_name: e.target.value})} placeholder="Karigar name" data-testid="po-supplier-input" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Input value={createForm.notes} onChange={e => setCreateForm({...createForm, notes: e.target.value})} placeholder="Optional notes" />
            </div>
            <div className="border-t pt-3">
              <label className="text-sm font-medium mb-2 block">Items ({createForm.items.length})</label>
              <div className="space-y-2 mb-3">
                {createForm.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg text-xs">
                    <span>{item.item_name} ({item.gold_purity || 'any'}) - {item.quantity_g}g</span>
                    <button onClick={() => removeItemFromForm(i)} className="text-muted-foreground hover:text-destructive"><X size={12} /></button>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input value={newItem.item_name} onChange={e => setNewItem({...newItem, item_name: e.target.value})} placeholder="Item name" className="text-xs" />
                <DecimalInput value={newItem.quantity_g} onChange={v => setNewItem({...newItem, quantity_g: v})} placeholder="Qty (g)" className="text-xs" />
                <div className="flex gap-1">
                  <Input value={newItem.gold_purity} onChange={e => setNewItem({...newItem, gold_purity: e.target.value})} placeholder="Purity" className="text-xs w-16" />
                  <Button size="sm" variant="outline" onClick={addItemToForm} className="text-xs"><Plus size={12} /></Button>
                </div>
              </div>
            </div>
            <Button onClick={handleCreatePO} disabled={loading} className="w-full" data-testid="po-create-submit">
              <ShoppingBag size={16} className="mr-2" />{loading ? 'Creating...' : 'Create Purchase Order'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PO Detail Dialog */}
      <Dialog open={!!selectedPO} onOpenChange={() => setSelectedPO(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>PO {selectedPO?.po_number}</DialogTitle></DialogHeader>
          {selectedPO && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Karigar:</span><br /><b>{selectedPO.supplier_name}</b></div>
                <div><span className="text-muted-foreground">Status:</span><br />{statusBadge(selectedPO.status)}</div>
                <div><span className="text-muted-foreground">Created:</span><br />{formatDateTime(selectedPO.created_at)}</div>
                <div><span className="text-muted-foreground">By:</span><br />{selectedPO.submitted_by}</div>
                {selectedPO.approved_by && <div><span className="text-muted-foreground">Approved by:</span><br />{selectedPO.approved_by}</div>}
              </div>
              {selectedPO.notes && <div className="p-2 rounded-lg bg-muted/50 text-xs">{selectedPO.notes}</div>}
              <Table className="data-table">
                <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Item</TableHead><TableHead>Purity</TableHead><TableHead className="text-right">Qty (g)</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(selectedPO.items || []).map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{item.item_name}</TableCell>
                      <TableCell>{item.gold_purity || '—'}</TableCell>
                      <TableCell className="text-right font-mono">{item.quantity_g}g</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => handlePrint(selectedPO)}>
                  <Printer size={14} className="mr-2" />Print
                </Button>
                {canApprove && selectedPO.status === 'pending' && (
                  <>
                    <Button variant="outline" className="text-emerald-600 border-emerald-200" onClick={() => handleApproval(selectedPO.id, 'approved')}>
                      <Check size={14} className="mr-2" />Approve
                    </Button>
                    <Button variant="outline" className="text-rose-600 border-rose-200" onClick={() => handleApproval(selectedPO.id, 'rejected')}>
                      <X size={14} className="mr-2" />Reject
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
